import { useMemo } from 'react'

/**
 * Core, headless types and logic for the DataTable component.
 * This file is intentionally styling-agnostic so it can be reused
 * in other UI layers or design systems.
 */

export interface DataTableColumn<T> {
  /** Unique column identifier and default accessor key */
  key: keyof T | string
  /** Column header content (UI layer can narrow to ReactNode) */
  header: unknown
  /** Custom cell renderer for this column (UI layer can narrow to ReactNode) */
  render?: (row: T, index: number) => unknown
  /** Current sort state announced from the column header */
  ariaSort?: 'none' | 'ascending' | 'descending' | 'other'
}

export interface UseDataTableOptions<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  /** Optional key extractor for row (default: index) */
  getRowKey?: (row: T, index: number) => string
}

export interface DataTableRow<T> {
  /** Stable row identifier */
  id: string
  /** Original row object */
  original: T
}

export interface UseDataTableResult<T> {
  columns: DataTableColumn<T>[]
  rows: DataTableRow<T>[]
  isEmpty: boolean
}

export function useDataTable<T>({
  columns,
  data,
  getRowKey = (_, index) => String(index),
}: UseDataTableOptions<T>): UseDataTableResult<T> {
  return useMemo(
    () => ({
      columns,
      rows: data.map((row, index) => ({
        id: getRowKey(row, index),
        original: row,
      })),
      isEmpty: data.length === 0,
    }),
    [columns, data, getRowKey]
  )
}
